import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import cn from '@/utils/cn';

type Tone = 'success' | 'warning' | 'destructive' | 'info';

const TONE_DOT: Record<Tone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  info: 'bg-primary',
};

const TONE_BADGE: Record<Tone, string> = {
  success: 'bg-success/10 text-success border-success/20',
  warning: 'bg-warning/20 text-foreground border-warning/40',
  destructive: 'bg-destructive/10 text-destructive border-destructive/30',
  info: 'bg-primary/10 text-primary border-primary/20',
};

const trendIcon = (delta?: string) => {
  if (!delta || delta === '-') return Minus;
  if (delta.trim().startsWith('-')) return ArrowDownRight;
  return ArrowUpRight;
};

interface KpiCardProps {
  label: string;
  value: string | number;
  delta?: string;
  tone?: Tone;
  className?: string;
}

export function KpiCard({ label, value, delta, tone = 'info', className }: KpiCardProps) {
  const Icon = trendIcon(delta);
  return (
    <Card
      padding="none"
      className={cn(
        'group relative overflow-hidden border-border/60 transition-all hover:border-primary/30 hover:shadow-md',
        className,
      )}
    >
      <span className={cn('absolute inset-x-0 top-0 h-0.5', TONE_DOT[tone])} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', TONE_DOT[tone])} />
        </div>
        <p
          title={String(value)}
          className="mt-3 truncate text-xl font-extrabold tracking-tight text-foreground tabular-nums sm:text-2xl"
        >
          {value}
        </p>
        {delta && (
          <span
            className={cn(
              'mt-3 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
              TONE_BADGE[tone],
            )}
          >
            <Icon className="h-3 w-3" />
            {delta}
          </span>
        )}
      </CardContent>
    </Card>
  );
}
